import { Injectable } from '@angular/core';
import { first } from 'lodash-es';
import { Logger } from '@se/se.ui.html5';

import {
    ScreenDetailed,
    getScreenDetails,
    isWindowManagementSupported,
    requestWindowManagementPermission,
} from './window-management';
import { interval, map } from 'rxjs';

export enum ScreenType {
    Primary,
    Extended,
}

export interface WindowFeatures {
    location?: boolean;
    menubar?: boolean;
    popup?: boolean;
    resizable?: boolean;
    scrollbars?: boolean;
    status?: boolean;
    toolbar?: boolean;
}

export enum WindowTarget {
    Blank = '_blank',
    Popup = 'popup',
    None = '',
}

export interface IMessageTransformer {
    decode(message: any): Promise<unknown>;
    encode(message: any): Promise<unknown>;
}

export function convertBoolFeature(feature: boolean) {
    return feature ? 'yes' : 'no';
}

/**
 * Riciclabile da qualche altra parte?
 */
export function buildQueryString(params: Record<string, string>) {
    let qs = '';
    for (const key in params) {
        if (Object.hasOwnProperty.call(params, key)) {
            const value = params[key];
            qs += `${key}=${value}&`;
        }
    }

    return qs.substring(0, qs.length - 1);
}

export class ChildWindowBuilder {
    private width?: number;
    private height?: number;
    private left?: number;
    private top?: number;

    private features = {};

    private messageTransformer?: IMessageTransformer;
    private screenPromise?: Promise<ScreenDetailed | undefined>;
    private target = WindowTarget.Blank;

    constructor(
        private logger: Logger,
        private url: string,
    ) {}

    atPosition(left: number, top: number) {
        this.left = left;
        this.top = top;

        return this;
    }

    onScreen(screenType: ScreenType) {
        if (!isWindowManagementSupported()) {
            return this;
        }

        this.screenPromise = requestWindowManagementPermission()
            .then(async (state) => {
                if (state === 'denied') {
                    return undefined;
                }
                const details = await getScreenDetails();
                // Consideriamo lo schermo primario come quello dove si trova il browser, e gli altri come secondari.
                if (screenType === ScreenType.Primary) {
                    return details.currentScreen;
                }
                const otherScreens = details.screens.filter((s) => s !== details.currentScreen);
                // Considera il primo schermo trovato come quello più idoneo, in futuro possibilmente considerare
                //   pixelDensity o dimensioni.
                return first(otherScreens) ?? details.currentScreen;
            })
            .catch((err) => {
                // Ignora errori causati dal posizionamento multi-monitor.
                this.logger.error(err);
                return undefined;
            });

        return this;
    }

    async open() {
        return new ChildWindow(this.url, this.target, await this.buildFeatures(), this.messageTransformer);
    }

    withFeatures(features: Partial<WindowFeatures>) {
        this.features = {
            ...this.features,
            ...features,
        };

        return this;
    }

    withMessageTransformer(transformer: IMessageTransformer) {
        this.messageTransformer = transformer;

        return this;
    }

    withSize(width: number, height: number) {
        this.width = width;
        this.height = height;

        return this;
    }

    withTarget(target: WindowTarget) {
        this.target = target;

        return this;
    }

    private async buildFeatures() {
        const features = [];
        const screen = await this.screenPromise;
        // Per LEFT e TOP bisogna aggiustare i valori nel caso sia stato specificato uno schermo.
        // Questo ha l'effetto di resettare il default nel caso non si voglia aprire la finestra sul bordo sinistro o
        //   alto, ma è accettabile.
        if (screen || this.left !== undefined) {
            const left = (screen?.availLeft ?? 0) + (this.left ?? 0);
            features.push(`left=${left}`);
        }
        if (screen || this.top !== undefined) {
            const top = (screen?.availTop ?? 0) + (this.top ?? 0);
            features.push(`top=${top}`);
        }
        if (screen || this.width !== undefined) {
            const width = this.width ?? screen?.availWidth ?? 0;
            features.push(`width=${width}`);
        }
        if (screen || this.height !== undefined) {
            const height = this.height ?? screen?.availWidth ?? 0;
            features.push(`height=${height}`);
        }

        const otherFeatures = Object.entries(this.features)
            .filter((([_, isEnabled]) => isEnabled !== undefined) as (kv) => kv is [string, boolean])
            .map(([feature, isEnabled]) => `${feature}=${convertBoolFeature(isEnabled)}`);

        return features.concat(otherFeatures);
    }
}

export class PopupsBlockedError extends Error {
    constructor() {
        super('Popups are blocked.');
    }
}

type SendMessageOptions = {
    targetOrigin?: string;
    skipTransform?: boolean;
};

export class ChildWindow {
    readonly window: Window;

    constructor(
        url: string,
        target: WindowTarget,
        features: string[],
        private messageTransformer?: IMessageTransformer,
    ) {
        const newWindow = window.open(url, target, features.join(','));
        if (newWindow === null) {
            throw new PopupsBlockedError();
        }
        this.window = newWindow;
    }

    async send(message: any, options: SendMessageOptions = {}) {
        const { skipTransform = false, targetOrigin = '*' } = options;

        if (!skipTransform && this.messageTransformer) {
            message = await this.messageTransformer.encode(message);
        }
        this.window.postMessage(message, targetOrigin);
    }

    close() {
        this.window.close();
    }

    closed$(checkInterval = 1000) {
        return interval(checkInterval).pipe(map(() => !this.window || this.window.closed));
    }
}

@Injectable({
    providedIn: 'root',
})
export class ChildWindowBuilderService {
    constructor(private logger: Logger) {}

    withUrl(url: string, params?: Record<string, string>) {
        if (params) {
            url = `${url}?${buildQueryString(params)}`;
        }

        return new ChildWindowBuilder(this.logger, url);
    }
}
